import React, { useState, useEffect, useRef } from "react";
import { useSnackbar } from "notistack";
import axios from "axios";
import SingleProduct from "../components/products/SingleProduct";
import PageWrapper from "../components/PageWrapper/PageWrapper";
import BackButton from "../components/BackButton";
import { useSearch } from "../contexts/SearchContext";

const SearchPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const [showOptions, setShowOptions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const {
    searchQuery,
    setSearchQuery,
    searchType,
    setSearchType,
    selectedArtist,
    setSelectedArtist,
    triggerSearchRef,
  } = useSearch();

  const { enqueueSnackbar } = useSnackbar();
  const auth = localStorage.getItem("authToken");

  const runSearch = async (query = searchQuery, artistId = selectedArtist) => {
    try {
      setLoading(true);
      setProducts([]);
      let res;
      switch (searchType) {
        case "productName":
          res = await axios.post(
            `${process.env.REACT_APP_BASEURL}/admin/product/search`,
            { word: query },
            { headers: { Authorization: `Bearer ${auth}` } }
          );
          break;
        case "artistName":
          res = await axios.post(
            `${process.env.REACT_APP_BASEURL}/admin/product/getbyartist`,
            { artist_id: artistId },
            { headers: { Authorization: `Bearer ${auth}` } }
          );
          break;
        case "trackName":
          res = await axios.post(
            `${process.env.REACT_APP_BASEURL}/admin/product/getbytrackname`,
            { word: query },
            { headers: { Authorization: `Bearer ${auth}` } }
          );
          break;
        default:
          break;
      }
      if (res?.data?.success) {
        const data = res.data.data;
        const list = Array.isArray(data) ? data : Object.values(data || {});
        setProducts(list);
      } else {
        enqueueSnackbar("No products found.", { variant: "info" });
        setProducts([]);
      }
    } catch (error) {
      console.log(error);
      enqueueSnackbar("Error occurred while searching products.", {
        variant: "error",
      });
      setProducts([]);
    } finally {
      setLoading(false);
      setShowOptions(false);
    }
  };

  useEffect(() => {
    triggerSearchRef.current = () => {
      runSearch(
        searchQuery,
        searchType === "artistName" ? selectedArtist : undefined
      );
    };
    return () => {
      triggerSearchRef.current = null;
    };
  }, [searchQuery, searchType, selectedArtist, triggerSearchRef]);

  // When landing from homepage (or elsewhere) with a query, run search once
  useEffect(() => {
    if (searchQuery.trim()) {
      runSearch(
        searchQuery,
        searchType === "artistName" ? selectedArtist : undefined
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchOptions = async () => {
      if (searchQuery.length >= 3 && searchType === "artistName") {
        try {
          const response = await axios.post(
            `${process.env.REACT_APP_BASEURL}/admin/product/searchartist`,
            { word: searchQuery },
            { headers: { Authorization: `Bearer ${auth}` } }
          );
          if (response.data.success) {
            setOptions(response.data.data);
            setShowOptions(true);
            setSelectedIndex(-1);
          } else {
            setOptions([]);
            setShowOptions(false);
          }
        } catch (error) {
          console.error(error);
          setOptions([]);
          setShowOptions(false);
        }
      } else {
        setOptions([]);
        setShowOptions(false);
      }
    };
    fetchOptions();
  }, [searchQuery, searchType, auth]);

  const handleTypeChange = (type) => {
    setSearchType(type);
    setSearchQuery("");
    setSelectedArtist(0);
    setShowOptions(false);
  };

  const handleOptionClick = (option) => {
    setSearchQuery(option.name);
    setSelectedArtist(option.artist_id);
    setShowOptions(false);
    runSearch(option.name, option.artist_id);
  };

  const handleSearchClick = () => {
    runSearch(
      searchQuery,
      searchType === "artistName" ? selectedArtist : undefined
    );
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearchClick();
    }
  };

  return (
    <PageWrapper>
      <div className="mt-6">
        <div className="mb-4">
          <BackButton />
        </div>
        {/* Search bar on search page */}
        <div className="flex justify-center mb-6">
          <div className="flex items-center w-full max-w-md border border-gray-400 rounded-md overflow-hidden bg-white shadow-sm">
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              placeholder={`Search by ${
                searchType === "productName"
                  ? "Product Name"
                  : searchType === "artistName"
                  ? "Artist Name"
                  : "Track Name"
              }...`}
              className="flex-1 py-2.5 px-4 focus:outline-none text-gray-800"
              autoComplete="off"
            />
            <button
              type="button"
              onClick={handleSearchClick}
              disabled={loading}
              className="px-4 py-2.5 bg-yellow-600 text-white font-medium hover:bg-yellow-700 disabled:opacity-60 transition"
            >
              {loading ? "Searching..." : "Search"}
            </button>
          </div>
        </div>

        {/* Product / Artist / Track type buttons and Search */}
        <div className="flex flex-wrap justify-center items-center gap-4 mb-6">
          <div className="flex items-center space-x-4">
            <input
              type="radio"
              id="product"
              name="searchType"
              value="productName"
              checked={searchType === "productName"}
              onChange={() => handleTypeChange("productName")}
              className="appearance-none h-3 w-3 border border-gray-300 rounded-md checked:bg-yellow-500 checked:border-transparent focus:outline-none focus:ring-2 focus:ring-yellow-500"
            />
            <label htmlFor="product" className="text-gray-700 cursor-pointer">
              Product
            </label>
          </div>
          <div className="flex items-center space-x-4">
            <input
              type="radio"
              id="artist"
              name="searchType"
              value="artistName"
              checked={searchType === "artistName"}
              onChange={() => handleTypeChange("artistName")}
              className="appearance-none h-3 w-3 border border-gray-300 rounded-md checked:bg-yellow-500 checked:border-transparent focus:outline-none focus:ring-2 focus:ring-yellow-500"
            />
            <label htmlFor="artist" className="text-gray-700 cursor-pointer">
              Artist
            </label>
          </div>
          <div className="flex items-center space-x-4">
            <input
              type="radio"
              id="track"
              name="searchType"
              value="trackName"
              checked={searchType === "trackName"}
              onChange={() => handleTypeChange("trackName")}
              className="appearance-none h-3 w-3 border border-gray-300 rounded-md checked:bg-yellow-500 checked:border-transparent focus:outline-none focus:ring-2 focus:ring-yellow-500"
            />
            <label htmlFor="track" className="text-gray-700 cursor-pointer">
              Track
            </label>
          </div>
        </div>

        {/* Artist suggestions (when type is Artist and user typed in navbar) */}
        {showOptions && searchType === "artistName" && options.length > 0 && (
          <div className="flex justify-center mb-4">
            <div className="w-full max-w-md bg-white border border-gray-300 rounded-md shadow-lg overflow-hidden">
              {options.map((option, index) => (
                <div
                  key={option.artist_id ?? index}
                  className={`px-4 py-2 cursor-pointer hover:bg-gray-100 ${
                    index === selectedIndex ? "bg-gray-200" : ""
                  }`}
                  onClick={() => handleOptionClick(option)}
                >
                  {option.name}
                </div>
              ))}
            </div>
          </div>
        )}

        {products.length > 0 ? (
          <main className="relative grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 items-stretch grid-auto-rows-[26rem] sm:grid-auto-rows-[28rem] md:grid-auto-rows-[30rem] xl:grid-auto-rows-[32rem]">
            {products.map((product) => {
              const id = product.product_id ?? product._id;
              let images = product.product_images ?? product.images;
              if (typeof images === "string") {
                try {
                  images = JSON.parse(images);
                } catch {
                  images = [];
                }
              }
              const image = Array.isArray(images) ? images[0] : images;
              return (
                <div key={id} className="h-full min-h-0">
                  <SingleProduct
                    product={{
                      _id: id,
                      qty: 0,
                      name: product.title ?? product.name,
                      description: product.description ?? "",
                      brand: product.category ?? product.brand,
                      category: "Sports",
                      gender: "Men",
                      weight: "200g",
                      quantity: product.qty_available ?? product.quantity ?? 0,
                      image: image,
                      rating: 4.5,
                      price: product.mrp ?? product.price,
                      newPrice: product.selling_price ?? product.newPrice,
                      trending: true,
                      label: product.label ?? "",
                      type: product.product_type ?? product.type,
                    }}
                    name="Search"
                  />
                </div>
              );
            })}
          </main>
        ) : (
          <p className="font-sans text-4xl font-bold uppercase tracking-wide text-gray-300 text-center w-full py-32">
            {loading ? "Searching..." : "Type in the search bar above and click Search"}
          </p>
        )}
      </div>
    </PageWrapper>
  );
};

export default SearchPage;
